import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, BookOpen, Loader2, ArrowRight, Share2, Copy, Check, Facebook, Twitter, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditorialPublicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Enlace copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('No se pudo copiar el enlace');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/public/editorial/${slug}`);
        setData(res.data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a12] flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-white/60 animate-spin" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a12] flex flex-col items-center justify-center text-center px-6" data-testid="editorial-public-notfound">
        <Building2 className="w-12 h-12 text-white/20 mb-4" />
        <h1 className="text-white gotham-bold text-2xl mb-2">Editorial no encontrada</h1>
        <p className="text-white/50 mb-6">No pudimos encontrar la editorial "{slug}".</p>
        <button onClick={() => navigate('/editoriales')}
          className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black gotham-bold">
          Crear tu editorial
        </button>
      </div>
    );
  }

  const brand = data.brand_color || '#f59e0b';

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white" data-testid="editorial-public-page">
      {/* Banner */}
      <div className="relative h-56 md:h-72 w-full overflow-hidden"
        style={data.banner_url ? {} : { background: `linear-gradient(135deg, ${brand}44, #0a0a12)` }}>
        {data.banner_url && (
          <img src={data.banner_url} alt="banner" className="w-full h-full object-cover" data-testid="editorial-public-banner" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a12] via-[#0a0a12]/40 to-transparent" />
      </div>

      {/* Header con logo */}
      <div className="max-w-5xl mx-auto px-6 -mt-16 relative">
        <div className="flex items-end gap-5 flex-wrap">
          <div className="w-28 h-28 rounded-2xl overflow-hidden border-4 border-[#0a0a12] bg-[#12121f] flex items-center justify-center shadow-xl"
            style={{ borderColor: '#0a0a12' }}>
            {data.logo_url
              ? <img src={data.logo_url} alt="logo" className="w-full h-full object-contain" data-testid="editorial-public-logo" />
              : <Building2 className="w-10 h-10" style={{ color: brand }} />}
          </div>
          <div className="pb-2">
            <h1 className="gotham-bold text-3xl md:text-4xl" data-testid="editorial-public-name">{data.editorial_name}</h1>
            <div className="mt-1 inline-flex items-center gap-2 text-sm">
              <span className="px-2.5 py-0.5 rounded-full text-black gotham-bold text-xs" style={{ backgroundColor: brand }}>
                {data.editorial_tier === 'premium' ? 'Premium' : 'Editorial'}
              </span>
              <span className="text-white/50">{data.total_works} obra{data.total_works === 1 ? '' : 's'}</span>
            </div>
          </div>
        </div>

        {data.editorial_description && (
          <p className="text-white/70 mt-6 max-w-3xl leading-relaxed" data-testid="editorial-public-description">
            {data.editorial_description}
          </p>
        )}

        {/* Compartir */}
        <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="editorial-share-bar">
          <span className="text-white/50 text-sm flex items-center gap-1.5 mr-1">
            <Share2 className="w-4 h-4" /> Compartir:
          </span>
          <button onClick={copyLink} data-testid="editorial-share-copy"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/80 transition-colors">
            {copied ? <><Check className="w-4 h-4 text-emerald-400" /> Copiado</> : <><Copy className="w-4 h-4" /> Copiar enlace</>}
          </button>
          <a href={`https://wa.me/?text=${encodeURIComponent(`${data.editorial_name} en Viubux — ${shareUrl}`)}`}
            target="_blank" rel="noreferrer" data-testid="editorial-share-whatsapp"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/30 text-sm text-[#4ade80] transition-colors">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
            target="_blank" rel="noreferrer" data-testid="editorial-share-facebook"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1877F2]/15 hover:bg-[#1877F2]/25 border border-[#1877F2]/30 text-sm text-[#60a5fa] transition-colors">
            <Facebook className="w-4 h-4" /> Facebook
          </a>
          <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`Descubre ${data.editorial_name} en Viubux`)}`}
            target="_blank" rel="noreferrer" data-testid="editorial-share-twitter"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white/80 transition-colors">
            <Twitter className="w-4 h-4" /> X
          </a>
        </div>
      </div>

      {/* Catálogo */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="gotham-bold text-xl mb-6 flex items-center gap-2">
          <BookOpen className="w-5 h-5" style={{ color: brand }} /> Catálogo
        </h2>
        {data.catalog.length === 0 ? (
          <p className="text-white/50" data-testid="editorial-public-empty">Esta editorial aún no tiene obras publicadas.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="editorial-public-catalog">
            {data.catalog.map((w) => (
              <div key={w.id} data-testid={`editorial-public-work-${w.id}`}
                className="bg-white/[0.04] border border-white/10 rounded-xl overflow-hidden hover:border-white/25 transition-colors group">
                <div className="aspect-[3/4] bg-black/40 flex items-center justify-center overflow-hidden">
                  {w.cover_url
                    ? <img src={w.cover_url} alt={w.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    : <BookOpen className="w-8 h-8 text-white/20" />}
                </div>
                <div className="p-3">
                  <div className="gotham-bold text-sm line-clamp-2">{w.title}</div>
                  {w.author_name && <div className="text-white/50 text-xs mt-1 line-clamp-1">{w.author_name}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto px-6 py-8 flex items-center justify-between flex-wrap gap-4">
          <span className="text-white/60 text-sm">Parte del Universo Viubux</span>
          <button onClick={() => navigate('/editoriales')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl gotham-bold text-black transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: brand }}
            data-testid="editorial-public-cta">
            Crea tu editorial <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditorialPublicPage;
